import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ClubApi } from "../../services/clubApi";
import { useAuthStatus } from "../../components/auth/AuthProvider";

type SessionStatusFilter = "" | "active" | "closed" | "expired";
const SESSION_AUTO_REFRESH_MS = 15000;
const SESSION_SHARE_STORAGE_KEY = "club_group_session_share_v1";

type SessionParticipant = {
  displayName?: string;
  joinedAt?: string | null;
};

type SessionRow = {
  id: string;
  caseIndex: number;
  caseName: string;
  ownerUserId: string;
  ownerFirstName: string;
  startsAt: string;
  expiresAt: string;
  durationHours: number;
  maxPlayers: number;
  participantCount: number;
  participants?: SessionParticipant[];
  status: string;
  closedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

type StoredSessionShare = {
  sessionId: string;
  guestUrl: string;
  inviteText: string;
  updatedAt: string;
};

type DeleteDialogState = {
  sessionId: string;
  caseName: string;
  status: string;
} | null;

const statusBadgeClass = (status: string) => {
  if (status === "active") return "border-emerald-300/35 bg-emerald-400/15 text-emerald-100";
  if (status === "closed") return "border-rose-300/35 bg-rose-400/15 text-rose-100";
  return "border-amber-300/35 bg-amber-400/15 text-amber-100";
};

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString();
};

const buildQrImageUrl = (value: string) => {
  const encoded = encodeURIComponent(value);
  return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encoded}`;
};

const canMemberHostGroupSessions = (member: any) => {
  const status = String(member?.status || "").trim().toLowerCase();
  if (!member || !status) return false;
  return status === "active";
};

const readStoredSessionShares = (): Record<string, StoredSessionShare> => {
  try {
    const raw = window.localStorage.getItem(SESSION_SHARE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, StoredSessionShare> | null;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  } catch {
    return {};
  }
};

const writeStoredSessionShares = (value: Record<string, StoredSessionShare>) => {
  try {
    window.localStorage.setItem(SESSION_SHARE_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage can be blocked in privacy modes.
  }
};

const ClubSessionManagePage = () => {
  const { isAdmin } = useAuthStatus();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState<SessionStatusFilter>("active");
  const [rows, setRows] = useState<SessionRow[]>([]);
  const [member, setMember] = useState<any>(null);
  const [closingSessionId, setClosingSessionId] = useState("");
  const [deletingSessionId, setDeletingSessionId] = useState("");
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>(null);
  const [shareBySessionId, setShareBySessionId] = useState<Record<string, StoredSessionShare>>({});
  const [copyFeedback, setCopyFeedback] = useState("");
  const [expandedQrSessionId, setExpandedQrSessionId] = useState("");

  useEffect(() => {
    setShareBySessionId(readStoredSessionShares());
  }, []);

  useEffect(() => {
    if (isAdmin) return;
    let cancelled = false;
    ClubApi.getMe()
      .then((response) => {
        if (cancelled) return;
        setMember({
          ...(response?.member || {}),
          subscription: response?.subscription || null,
        });
      })
      .catch(() => {
        if (!cancelled) setMember(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const loadSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await ClubApi.listGroupSessions({
        page: 1,
        limit: 100,
        status: statusFilter,
      });
      setRows(Array.isArray(response?.sessions) ? response.sessions : []);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load sessions.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        loadSessions();
      }
    }, SESSION_AUTO_REFRESH_MS);
    return () => window.clearInterval(interval);
  }, [loadSessions]);

  const closeSession = async (sessionId: string) => {
    if (!sessionId) return;
    try {
      setClosingSessionId(sessionId);
      setError("");
      const response = await ClubApi.closeGroupSession(sessionId);
      setRows((previous) =>
        previous.map((row) =>
          row.id !== sessionId
            ? row
            : {
                ...row,
                status: String(response?.session?.status || "closed"),
                closedAt: response?.session?.closedAt || new Date().toISOString(),
              }
        )
      );
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to close session.");
    } finally {
      setClosingSessionId("");
    }
  };

  const deleteSession = async (sessionId: string) => {
    if (!sessionId) return;

    try {
      setDeletingSessionId(sessionId);
      setError("");
      await ClubApi.deleteGroupSession(sessionId);
      setRows((previous) => previous.filter((row) => row.id !== sessionId));
      setExpandedQrSessionId((previous) => (previous === sessionId ? "" : previous));
      setShareBySessionId((previous) => {
        if (!previous[sessionId]) return previous;
        const next = { ...previous };
        delete next[sessionId];
        writeStoredSessionShares(next);
        return next;
      });
      setDeleteDialog((previous) => (previous?.sessionId === sessionId ? null : previous));
      setCopyFeedback(`Deleted session ${sessionId}.`);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to delete session.");
    } finally {
      setDeletingSessionId("");
    }
  };

  const handleCopyLink = async (sessionId: string) => {
    const share = shareBySessionId[sessionId];
    const url = String(share?.guestUrl || "").trim();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopyFeedback(`Copied link for session ${sessionId}.`);
    } catch {
      setCopyFeedback("Copy failed.");
    }
  };

  const handleCopyInvite = async (sessionId: string) => {
    const share = shareBySessionId[sessionId];
    const url = String(share?.guestUrl || "").trim();
    const inviteText = String(share?.inviteText || "").trim();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(inviteText ? `${inviteText}\n${url}` : url);
      setCopyFeedback(`Copied invite for session ${sessionId}.`);
    } catch {
      setCopyFeedback("Copy failed.");
    }
  };

  const handleCopyQr = async (sessionId: string) => {
    const share = shareBySessionId[sessionId];
    const url = String(share?.guestUrl || "").trim();
    if (!url) return;
    const qrUrl = buildQrImageUrl(url);

    try {
      const ClipboardItemCtor = (window as any).ClipboardItem;
      if (navigator.clipboard?.write && typeof ClipboardItemCtor !== "undefined") {
        const response = await fetch(qrUrl);
        const qrBlob = await response.blob();
        const item = new ClipboardItemCtor({
          [qrBlob.type || "image/png"]: qrBlob,
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(qrUrl);
      }
      setCopyFeedback(`Copied QR for session ${sessionId}.`);
    } catch {
      setCopyFeedback("Copy failed.");
    }
  };

  const title = isAdmin ? "Session Admin" : "My Sessions";
  const subtitle = isAdmin
    ? "Monitor and close active group sessions to prevent orphan links."
    : "Manage your active and recent group sessions.";
  const activeCount = useMemo(
    () => rows.filter((row) => String(row.status || "") === "active").length,
    [rows]
  );
  const sessionHostingAllowed = isAdmin || canMemberHostGroupSessions(member);

  return (
    <section className="mx-auto max-w-6xl px-4 py-6 text-white">
      <div className="rounded-3xl border border-cyan-300/20 bg-gradient-to-br from-[#0a1222] via-[#11182a] to-[#231417] p-6 shadow-[0_30px_90px_rgba(8,18,40,0.45)]">
        <p className="text-xs uppercase tracking-[0.3em] text-cyan-200">{isAdmin ? "Admin" : "Member"}</p>
        <h1 className="mt-2 text-3xl font-black">{title}</h1>
        <p className="mt-2 text-sm text-zinc-200">{subtitle}</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {sessionHostingAllowed ? (
            <Link
              to="/agency/sessions/new"
              className="rounded-lg bg-cyan-300 px-4 py-2 text-sm font-semibold text-[#0b1322]"
            >
              Start Group Session
            </Link>
          ) : (
            <div className="rounded-lg border border-amber-300/30 bg-amber-300/10 px-4 py-2 text-sm font-semibold text-amber-100">
              Reactivate membership to host new group sessions
            </div>
          )}
          <Link
            to="/agency/dashboard"
            className="rounded-lg border border-white/25 px-4 py-2 text-sm font-semibold"
          >
            Back to Dashboard
          </Link>
          <div className="rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-xs font-semibold">
            Active now: {activeCount}
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-white/15 bg-zinc-950/70 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-bold">Sessions</h2>
          <div className="flex items-center gap-2">
            <label htmlFor="session-status-filter" className="text-xs font-semibold uppercase tracking-wider text-zinc-300">
              Status
            </label>
            <select
              id="session-status-filter"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as SessionStatusFilter)}
              className="rounded-lg border border-white/20 bg-zinc-900 px-3 py-2 text-sm"
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
              <option value="expired">Expired</option>
            </select>
            <button
              type="button"
              onClick={loadSessions}
              className="rounded-lg border border-white/25 px-3 py-2 text-sm font-semibold hover:bg-white/10"
            >
              Refresh
            </button>
          </div>
        </div>

        {loading ? <p className="mt-4 text-sm text-zinc-300">Loading sessions...</p> : null}
        {!loading && error ? <p className="mt-4 text-sm text-rose-300">{error}</p> : null}
        {!loading && !error && copyFeedback ? <p className="mt-4 text-sm text-emerald-300">{copyFeedback}</p> : null}
        {!loading && !error && rows.length === 0 ? (
          <p className="mt-4 text-sm text-zinc-300">No sessions found for this filter.</p>
        ) : null}

        {!loading && !error && rows.length > 0 ? (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wider text-zinc-300">
                <tr>
                  <th className="pb-2 pr-4">Case</th>
                  {isAdmin ? <th className="pb-2 pr-4">Host</th> : null}
                  <th className="pb-2 pr-4">Window</th>
                  <th className="pb-2 pr-4">Players</th>
                  <th className="pb-2 pr-4">Status</th>
                  <th className="pb-2 pr-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const status = String(row?.status || "");
                  const canClose = status === "active";
                  const canDelete = status === "closed" || status === "expired";
                  const hasSavedShare = Boolean(String(shareBySessionId[row.id]?.guestUrl || "").trim());
                  return (
                    <tr key={row.id} className="border-t border-white/10 align-top">
                      <td className="py-3 pr-4">
                        <div className="font-semibold">{row.caseName}</div>
                        <div className="text-xs text-zinc-400">Case #{row.caseIndex}</div>
                        {hasSavedShare ? (
                          <div className="mt-2 text-xs">
                            <a
                              href={shareBySessionId[row.id].guestUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="break-all text-cyan-200 underline decoration-cyan-300/70 underline-offset-2"
                            >
                              {shareBySessionId[row.id].guestUrl}
                            </a>
                          </div>
                        ) : !isAdmin ? (
                          <div className="mt-2 text-xs">
                            <span className="text-zinc-500">
                              No saved share link on this device.
                            </span>
                          </div>
                        ) : null}
                      </td>
                      {isAdmin ? (
                        <td className="py-3 pr-4">
                          <div className="font-semibold">{row.ownerFirstName || "Detective"}</div>
                          <div className="text-xs text-zinc-400">{row.ownerUserId}</div>
                        </td>
                      ) : null}
                      <td className="py-3 pr-4">
                        <div>{formatDateTime(String(row.startsAt || ""))}</div>
                        <div className="text-xs text-zinc-400">to {formatDateTime(String(row.expiresAt || ""))}</div>
                      </td>
                      <td className="py-3 pr-4">
                        <div>
                          {row.participantCount}/{row.maxPlayers}
                        </div>
                        {Array.isArray(row.participants) && row.participants.length > 0 ? (
                          <div className="mt-1 text-xs text-zinc-300">
                            {row.participants
                              .map((participant) => String(participant?.displayName || "").trim())
                              .filter((name) => name.length > 0)
                              .join(", ")}
                          </div>
                        ) : (
                          <div className="mt-1 text-xs text-zinc-500">No registered players yet.</div>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <span
                          className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${statusBadgeClass(
                            status
                          )}`}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex flex-wrap items-center gap-2">
                          {hasSavedShare ? (
                            <>
                              <button
                                type="button"
                                onClick={() => handleCopyLink(String(row.id || ""))}
                                className="rounded-lg border border-white/20 px-3 py-1.5 text-xs font-semibold"
                              >
                                Copy Link
                              </button>
                              <button
                                type="button"
                                onClick={() => handleCopyInvite(String(row.id || ""))}
                                className="rounded-lg border border-white/20 px-3 py-1.5 text-xs font-semibold"
                              >
                                Copy Invite
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedQrSessionId((previous) =>
                                    previous === row.id ? "" : row.id
                                  )
                                }
                                className="rounded-lg border border-white/20 px-3 py-1.5 text-xs font-semibold"
                              >
                                {expandedQrSessionId === row.id ? "Hide QR" : "Show QR"}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleCopyQr(String(row.id || ""))}
                                className="rounded-lg border border-white/20 px-3 py-1.5 text-xs font-semibold"
                              >
                                Copy QR
                              </button>
                            </>
                          ) : null}
                          <button
                            type="button"
                            disabled={!canClose || closingSessionId === row.id}
                            onClick={() => closeSession(String(row.id || ""))}
                            className="rounded-lg border border-rose-300/35 bg-rose-500/10 px-3 py-1.5 text-xs font-semibold text-rose-100 disabled:cursor-not-allowed disabled:opacity-55"
                          >
                            {closingSessionId === row.id ? "Closing..." : canClose ? "Close Session" : "Closed"}
                          </button>
                          {canDelete ? (
                            <button
                              type="button"
                              disabled={deletingSessionId === row.id}
                              onClick={() =>
                                setDeleteDialog({
                                  sessionId: String(row.id || ""),
                                  caseName: String(row.caseName || "Untitled Session"),
                                  status,
                                })
                              }
                              className="rounded-lg border border-amber-300/35 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-100 disabled:cursor-not-allowed disabled:opacity-55"
                            >
                              {deletingSessionId === row.id ? "Deleting..." : "Delete Session"}
                            </button>
                          ) : null}
                        </div>
                        {hasSavedShare &&
                        expandedQrSessionId === row.id ? (
                          <div className="mt-3 rounded-lg border border-white/15 bg-white/5 p-2">
                            <img
                              src={buildQrImageUrl(shareBySessionId[row.id].guestUrl)}
                              alt="Session QR code"
                              className="w-28 rounded bg-white p-1"
                            />
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {deleteDialog ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-4 backdrop-blur-md"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-session-title"
        >
          <div className="relative w-full max-w-xl overflow-hidden rounded-[30px] border border-white/12 bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.14),_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(251,146,60,0.14),_transparent_36%),linear-gradient(145deg,rgba(8,12,22,0.98),rgba(20,10,18,0.97))] p-6 shadow-[0_40px_120px_rgba(0,0,0,0.55)]">
            <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-cyan-200/75 to-transparent" />
            <p className="text-[11px] uppercase tracking-[0.34em] text-cyan-200/85">Field Operations Control</p>
            <h3 id="delete-session-title" className="mt-3 text-3xl font-black text-white">
              Delete this session record?
            </h3>
            <p className="mt-3 max-w-lg text-sm leading-7 text-zinc-200">
              You are removing the session for{" "}
              <span className="font-semibold text-white">{deleteDialog.caseName}</span>. This clears the saved
              share-link state on this device and deletes the room record permanently.
            </p>

            <div className="mt-6 grid gap-3 sm:grid-cols-[1fr_1.2fr]">
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3">
                <p className="text-[11px] uppercase tracking-[0.28em] text-zinc-400">Session Status</p>
                <p className="mt-2 text-base font-semibold capitalize text-white">{deleteDialog.status}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3">
                <p className="text-[11px] uppercase tracking-[0.28em] text-zinc-400">Deletion Rule</p>
                <p className="mt-2 text-sm font-medium text-zinc-100">
                  Only closed or expired sessions can be deleted. Active sessions stay protected.
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-amber-200/15 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">
              Use deletion for stale completed rooms after review. This action does not delete the underlying case.
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteDialog(null)}
                disabled={deletingSessionId === deleteDialog.sessionId}
                className="rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-zinc-100 transition hover:border-white/30 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Keep Session
              </button>
              <button
                type="button"
                onClick={() => deleteSession(deleteDialog.sessionId)}
                disabled={deletingSessionId === deleteDialog.sessionId}
                className="rounded-full border border-rose-300/40 bg-gradient-to-r from-rose-500/90 to-orange-400/90 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_14px_34px_rgba(244,63,94,0.28)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletingSessionId === deleteDialog.sessionId ? "Deleting..." : "Delete Session"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ClubSessionManagePage;
